import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { Bot, Target, ListOrdered, Heart, CheckSquare, ListTodo, FileText, Calendar, Save, Settings, Star } from "lucide-react";

interface AICoachSettings {
  includeGoals: boolean;
  includeAffirmations: boolean;
  includeGratefuls: boolean;
  includeHabits: boolean;
  includeTasks: boolean;
  includeJournal: boolean;
  includeCalendar: boolean;
  includeImportantDates: boolean;
}

const defaultSettings: AICoachSettings = {
  includeGoals: true,
  includeAffirmations: true,
  includeGratefuls: true,
  includeHabits: true,
  includeTasks: true,
  includeJournal: true,
  includeCalendar: true,
  includeImportantDates: true,
};

export default function AICoachSettingsPage() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [settings, setSettings] = useState<AICoachSettings>(() => {
    const saved = localStorage.getItem('ai-coach-settings');
    return saved ? JSON.parse(saved) : defaultSettings;
  });

  const handleToggle = (key: keyof AICoachSettings) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = () => {
    localStorage.setItem('ai-coach-settings', JSON.stringify(settings));
    toast({
      title: "Settings saved",
      description: "Your AI Coach settings have been updated.",
    });
  };

  const settingsItems = [
    { key: "includeGoals" as const, label: "My Goals", description: "Include your goals and progress in AI coaching", icon: Target, constraintsRoute: "/ai-coach/constraints/goals" },
    { key: "includeAffirmations" as const, label: "Affirmations", description: "Include your daily affirmations", icon: ListOrdered, constraintsRoute: "/ai-coach/constraints/affirmations" },
    { key: "includeGratefuls" as const, label: "Gratefuls", description: "Include your gratitude entries", icon: Heart, constraintsRoute: "/ai-coach/constraints/gratefuls" },
    { key: "includeHabits" as const, label: "Habits", description: "Include your habit tracking data", icon: CheckSquare, constraintsRoute: "/ai-coach/constraints/habits" },
    { key: "includeTasks" as const, label: "Tasks", description: "Include your tasks and to-dos", icon: ListTodo, constraintsRoute: "/ai-coach/constraints/tasks" },
    { key: "includeJournal" as const, label: "Journal", description: "Include your journal entries", icon: FileText, constraintsRoute: "/ai-coach/constraints/journal" },
    { key: "includeCalendar" as const, label: "Calendar", description: "Include your calendar events", icon: Calendar, constraintsRoute: "/ai-coach/constraints/calendar" },
    { key: "includeImportantDates" as const, label: "Important Dates", description: "Include birthdays, anniversaries, and special occasions", icon: Star, constraintsRoute: "/calendar" },
  ];

  return (
    <div className="w-full max-w-full p-4 md:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Bot className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-ai-coach-title">AI Coach Settings</h1>
          <p className="text-muted-foreground">Configure what data the AI Coach can access</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Data Sources</CardTitle>
          <CardDescription>
            Toggle which areas of your account the AI Coach should include when providing personalized coaching and insights.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {settingsItems.map((item) => {
            const Icon = item.icon;
            const isEnabled = settings[item.key];
            return (
              <div key={item.key} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-muted rounded-md flex items-center justify-center">
                    <Icon className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div>
                    <Label htmlFor={item.key} className="text-base font-medium cursor-pointer">
                      {item.label}
                    </Label>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    id={item.key}
                    checked={isEnabled}
                    onCheckedChange={() => handleToggle(item.key)}
                    data-testid={`switch-${item.key}`}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={!isEnabled}
                    onClick={() => setLocation(item.constraintsRoute)}
                    className={`h-8 w-8 ${!isEnabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-muted'}`}
                    data-testid={`button-constraints-${item.key}`}
                  >
                    <Settings className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </div>
              </div>
            );
          })}

          <div className="pt-4 border-t">
            <Button onClick={handleSave} className="w-full" data-testid="button-save-ai-coach-settings">
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
